<script setup lang="ts">
import { computed } from 'vue';
import { marked } from 'marked';
import type { Message } from '../types/chat';
import { defaults } from '../messages';
import { sanitizeStreamingMarkdown } from '../utils/sanitizeStreamingMarkdown';
import ellaAvatar from '../assets/ella-avatar.svg?url';
import FeedbackRating from './FeedbackRating.vue';
import FeedbackComment from './FeedbackComment.vue';

interface Props {
    message: Message;
    allowFeedback?: boolean;
}

const MARKDOWN_RENDER_INTERVAL = 200;

const props = withDefaults(defineProps<Props>(), {
    allowFeedback: true,
});

const emit = defineEmits<{
    linkClick: [element: HTMLAnchorElement];
}>();

const renderer = new marked.Renderer();
const linkRenderer = renderer.link;
renderer.link = (token) => {
    const html = linkRenderer.call(renderer, token);
    return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

marked.setOptions({
    breaks: true,
    gfm: true,
    renderer,
});

const messageName = computed(() => {
    if (props.message.role === 'assistant') return 'Ella';
    if (props.message.role === 'user') return 'Du';
});

let lastRenderTime = 0;
let cachedResult = '';

const parsedContent = computed(() => {
    const rawContent = props.message.content;
    if (!rawContent) return '';
    if (props.message.role === 'user') return rawContent;

    if (!props.message.isStreaming) {
        return marked.parse(rawContent) as string;
    }

    const now = Date.now();
    if (now - lastRenderTime < MARKDOWN_RENDER_INTERVAL) {
        return cachedResult;
    }

    lastRenderTime = now;
    cachedResult = marked.parse(sanitizeStreamingMarkdown(rawContent)) as string;
    return cachedResult;
});

const shouldShowFeedback = computed(() => {
    return props.allowFeedback
        && props.message.role === 'assistant'
        && !props.message.isStreaming
        && Boolean(props.message.content);
});

const isFailureMessage = computed(() => {
    return props.message.content === defaults.failure;
});

const reloadPage = () => {
    window.location.reload();
};

const formatTime = (date: Date) => {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const formatTimeAttribute = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const handleLinkClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.nodeName === 'A') {
        emit('linkClick', target as HTMLAnchorElement);
    }
};
</script>

<template>
    <section
        :class="[
            'chat-message',
            `chat-message--${message.role}`,
            { 'chat-message--loading': !message.content },
        ]"
    >
        <span v-if="message.isStreaming" class="visually-hidden">Ella schreibt gerade eine Antwort.</span>

        <img
            v-if="message.role === 'assistant'"
            :src="ellaAvatar"
            alt=""
            class="chat-message__avatar"
        />

        <div class="chat-message__body" :aria-hidden="message.isStreaming ? 'true' : undefined">
            <h3 v-if="messageName" class="chat-message__name">
                {{ messageName }}
            </h3>

            <div class="chat-message__bubble">
                <slot name="bubble">
                    <template v-if="message.content">
                        <div
                            class="chat-message__content"
                            v-html="parsedContent"
                            @click.capture="handleLinkClick"
                        ></div>

                        <button
                            v-if="isFailureMessage"
                            type="button"
                            class="btn btn-primary mt-3"
                            @click="reloadPage"
                        >
                            Seite neu laden
                        </button>
                    </template>

                    <div v-else class="chat-message__loading" aria-hidden="true">
                        <span></span><span></span><span></span>
                    </div>
                </slot>
            </div>

            <div v-if="!message.isStreaming" class="chat-message__footer-wrapper">
                <div class="chat-message__footer">
                    <p class="chat-message__disclaimer" v-if="shouldShowFeedback">Generiert um <time :datetime="formatTimeAttribute(message.timestamp)">{{ formatTime(message.timestamp) }}</time>. KI kann Fehler machen.</p>
                    <time v-else
                        class="chat-message__time"
                        :datetime="formatTimeAttribute(message.timestamp)"
                        >
                        {{ formatTime(message.timestamp) }}
                    </time>

                    <FeedbackRating v-if="shouldShowFeedback" :message />
                </div>

                <FeedbackComment v-if="shouldShowFeedback" :message />
            </div>
        </div>
    </section>
</template>
